import React, { useState } from 'react';
import { listingLink } from '../domain/listingLifecycle';
import type { Listing } from '../domain/listings';
import GroupLinkDialog from './GroupLinkDialog';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Check, Copy, Link2 } from 'lucide-react';

/**
 * Share a listing (016 FR-028, research C22). A listing link is never a way
 * in, so the way to show it to someone new is an invite; the link itself is
 * for people who can already see it.
 */
export default function ShareListingDialog({
  listing,
  open,
  onOpenChange,
}: {
  listing: Listing;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [inviting, setInviting] = useState(false);
  const [copy, setCopy] = useState<'idle' | 'copied' | 'failed'>('idle');
  const link = listingLink(listing.id, window.location.origin);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopy('copied');
    } catch {
      setCopy('failed');
    }
  };

  return (
    <>
      <Dialog open={open && !inviting} onOpenChange={onOpenChange}>
        <DialogContent className="rounded-3xl border-none bg-background">
          <DialogHeader>
            <DialogTitle className="text-xl">Share this listing</DialogTitle>
            <DialogDescription>
              To show it to someone who can't see {listing.collection.name} yet, invite them.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6">
            <Button className="w-full rounded-full" onClick={() => setInviting(true)}>
              <Link2 />
              Make a group link
            </Button>
            <div className="space-y-2">
              <Button variant="secondary" className="w-full rounded-full" onClick={copyLink}>
                {copy === 'copied' ? <Check /> : <Copy />}
                {copy === 'copied' ? 'Copied' : 'Copy link'}
              </Button>
              <p className="text-sm text-muted-foreground">
                Only people who can already see this listing can open the link.
              </p>
              {copy === 'failed' && (
                <div>
                  <Label htmlFor="listing-link">Couldn't copy — here's the link</Label>
                  <Input
                    id="listing-link"
                    readOnly
                    value={link}
                    onFocus={(e) => e.currentTarget.select()}
                    className="mt-1 font-mono text-xs"
                  />
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
      {inviting && (
        <GroupLinkDialog
          open
          onOpenChange={(next) => {
            if (!next) {
              setInviting(false);
              onOpenChange(false);
            }
          }}
          preselectedCollectionId={listing.collection.id}
        />
      )}
    </>
  );
}
